import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from 'framer-motion'
import { useRef, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { Footer } from '@/components/Footer'
import { Navbar } from '@/components/Navbar'
import { HeroScene } from '@/components/HeroScene'
import { Typewriter } from '@/components/ui/typewriter'

const ease: [number, number, number, number] = [0.16, 1, 0.3, 1]

const products = [
  {
    name: 'TurboRAG',
    marker: 'Retrieval',
    line: 'Fast retrieval with exact context.',
    detail: 'Built for systems where recall and latency both matter.',
    status: 'frontier retrieval'
  },
  {
    name: 'A/MCL',
    marker: 'Memory',
    line: 'Automatic memory for agent continuity.',
    detail: 'Tracks what happened, why it mattered, and what comes next.',
    status: 'agent context'
  },
  {
    name: 'PKM',
    marker: 'Model',
    line: 'A model built from scratch with a novel architecture.',
    detail: 'The lab is pushing beyond tooling into original model research.',
    status: 'novel architecture'
  },
  {
    name: 'Jpdz',
    marker: 'Chat',
    line: 'A polished assistant for serious technical work.',
    detail: 'Research, direction, and developer acceleration in one surface.',
    status: 'developer assistant'
  },
  {
    name: 'Skill Composer',
    marker: 'Automation',
    line: 'Chain AI skills into multi-step workflows automatically.',
    detail: 'Detects when tasks benefit from sequential skill execution and orchestrates them.',
    status: 'skill orchestrator'
  },
  {
    name: 'Alphagen',
    marker: 'Finance',
    line: 'Quantitative prediction platform for market intelligence.',
    detail: 'NLP-based earnings analysis and ML-powered stock predictions for advanced financial intelligence.',
    status: 'financial intelligence'
  }
]

const thesis = [
  {
    label: '01',
    title: 'Push the frontier, then make it usable',
    body: 'Move capability forward, then make it real in the hands of a builder.'
  },
  {
    label: '02',
    title: 'Assist developers at the point of work',
    body: 'Retrieval, memory, chat, dashboards, and model research should reduce drag where work is happening.'
  },
  {
    label: '03',
    title: 'Ship products with taste and force',
    body: 'Interface, architecture, docs, and runtime should carry the same discipline.'
  }
]

const method = [
  ['Research', 'Study the bottleneck until the waste is obvious.'],
  ['Build', 'Turn the useful core into a product developers trust.'],
  ['Refine', 'Measure, simplify, document, and remove friction.']
]

function MouseGlow() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (ref.current) {
        ref.current.style.background = `radial-gradient(900px circle at ${e.clientX}px ${e.clientY}px, rgba(110,31,27,0.05), transparent 50%)`
      }
    }
    window.addEventListener('mousemove', onMove)
    return () => window.removeEventListener('mousemove', onMove)
  }, [])

  return <div ref={ref} className="pointer-events-none fixed inset-0 z-30 hidden lg:block" />
}

function Marquee() {
  const items = ['TurboRAG', 'A/MCL', 'PKM', 'Jpdz', 'Skill Composer', 'Alphagen', 'Frontier AI', 'Research', 'Developer Tools']

  return (
    <div className="overflow-hidden border-y border-[#211d18]/8 bg-[#eee8dc] py-5">
      <div className="flex animate-marquee">
        {[0, 1].map(set => (
          <div key={set} className="flex shrink-0 items-center">
            {items.map((name, j) => (
              <span key={`${set}-${j}`} className="flex items-center">
                <span className="whitespace-nowrap px-8 font-serif text-lg italic text-[#211d18]/50 sm:text-xl">{name}</span>
                <span className="h-1 w-1 shrink-0 rotate-45 bg-[#6e1f1b]/45" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function ProductAtelier({ reduceMotion }: { reduceMotion: boolean }) {
  const [activeProduct, setActiveProduct] = useState(0)
  const product = products[activeProduct]

  return (
    <div className="relative mx-auto w-full max-w-[1040px]">
      <div className="absolute -left-6 top-8 hidden h-[84%] w-px bg-[#211d18]/8 lg:block" />
      <div className="border border-[#211d18]/10 bg-[#f7f1e7]/80 p-3 shadow-[0_40px_100px_rgba(34,26,18,0.1)] sm:p-5 lg:p-6">
        <div className="border border-[#211d18]/8 bg-[#eee8dc]">
          <div className="grid border-b border-[#211d18]/8 lg:grid-cols-[0.62fr_1.38fr]">
            <div className="border-b border-[#211d18]/8 p-4 sm:p-5 lg:border-b-0 lg:border-r">
              <p className="mb-6 font-body text-[10px] uppercase tracking-[0.24em] text-[#6e1f1b]/60 sm:mb-8">
                Product index
              </p>
              <div className="space-y-1.5">
                {products.map((item, index) => (
                  <motion.button
                    key={item.name}
                    type="button"
                    onClick={() => setActiveProduct(index)}
                    onMouseEnter={() => setActiveProduct(index)}
                    whileHover={reduceMotion ? undefined : { x: 3 }}
                    whileTap={reduceMotion ? undefined : { scale: 0.995 }}
                    className={`group flex min-h-[3.2rem] w-full items-center justify-between border px-4 py-2.5 text-left transition-all duration-300 ${
                      activeProduct === index
                        ? 'border-[#211d18] bg-[#211d18] text-[#eee8dc]'
                        : 'border-transparent bg-transparent text-[#211d18] hover:border-[#211d18]/14 hover:bg-[#f7f1e7]'
                    }`}
                  >
                    <span className="font-serif text-xl leading-none">{item.name}</span>
                    <span className={`font-body text-[9px] uppercase tracking-[0.2em] transition-colors ${
                      activeProduct === index ? 'text-[#d6c9b6]' : 'text-[#6b6157]/60'
                    }`}>
                      {item.marker}
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>

            <div className="min-h-[340px] overflow-hidden p-5 sm:min-h-[420px] sm:p-7 lg:p-9">
              <AnimatePresence mode="wait">
                <motion.div
                  key={product.name}
                  initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
                  transition={{ duration: 0.4, ease }}
                >
                  <div className="mb-10 flex items-start justify-between gap-4 sm:mb-14">
                    <div className="min-w-0 flex-1">
                      <motion.p
                        initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, delay: 0.04, ease }}
                        className="mb-3 font-body text-[10px] uppercase tracking-[0.24em] text-[#6e1f1b]/60"
                      >
                        {product.status}
                      </motion.p>
                      <motion.h2
                        initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, delay: 0.08, ease }}
                        className="font-serif text-[clamp(2.6rem,9vw,5rem)] font-medium leading-[0.88] text-[#211d18] [overflow-wrap:anywhere]"
                      >
                        {product.name}
                      </motion.h2>
                    </div>
                    <motion.span
                      initial={reduceMotion ? { opacity: 1 } : { opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.12, ease }}
                      className="hidden font-body text-[10px] uppercase tracking-[0.2em] text-[#6b6157]/50 md:inline-flex"
                    >
                      0{activeProduct + 1} / 06
                    </motion.span>
                  </div>

                  <div className="grid gap-4 border-t border-[#211d18]/8 pt-6 sm:gap-5">
                    <motion.p
                      initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.1, ease }}
                      className="max-w-xl font-body text-lg leading-[1.75] text-[#40382f]"
                    >
                      {product.line}
                    </motion.p>
                    <motion.p
                      initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.16, ease }}
                      className="max-w-lg font-body text-[15px] leading-[1.8] text-[#6b6157]"
                    >
                      {product.detail}
                    </motion.p>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="mt-10 grid grid-cols-6 gap-1.5 sm:mt-14">
                {products.map((item, index) => (
                  <motion.button
                    key={item.name}
                    type="button"
                    aria-label={`Show ${item.name}`}
                    onClick={() => setActiveProduct(index)}
                    whileHover={reduceMotion ? undefined : { scaleY: 1.5 }}
                    className={`h-[3px] transition-all duration-300 ${
                      activeProduct === index ? 'bg-[#6e1f1b]' : 'bg-[#211d18]/10 hover:bg-[#211d18]/24'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="grid divide-y divide-[#211d18]/8 md:grid-cols-3 md:divide-x md:divide-y-0">
            {[
              ['Lab direction', 'Frontier tools'],
              ['Primary audience', 'Developers'],
              ['Operating taste', 'Elegant systems']
            ].map(([label, value]) => (
              <div key={label} className="p-4 sm:p-5">
                <p className="mb-2 font-body text-[10px] uppercase tracking-[0.22em] text-[#6e1f1b]/55">{label}</p>
                <p className="font-serif text-2xl text-[#211d18]">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export function LandingPage() {
  const containerRef = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  })

  const heroOpacity = useTransform(scrollYProgress, [0, 0.13], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.13], [1, 0.97])

  return (
    <div ref={containerRef} className="min-h-screen overflow-hidden bg-[#eee8dc] text-[#211d18]">
      <MouseGlow />

      <Navbar variant="light" />

      {/* ── Hero ── */}
      <motion.section
        style={reduceMotion ? undefined : { opacity: heroOpacity, scale: heroScale }}
        className="relative min-h-[96svh] overflow-hidden pt-28 sm:pt-36"
      >
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              'linear-gradient(90deg, rgba(33,29,24,0.06) 1px, transparent 1px), linear-gradient(rgba(33,29,24,0.045) 1px, transparent 1px)',
            backgroundSize: '80px 80px'
          }}
        />
        <div className="pointer-events-none absolute right-[5%] top-[15%] hidden h-[600px] w-[600px] rounded-full lg:block" style={{ background: 'radial-gradient(circle, rgba(110,31,27,0.06) 0%, rgba(200,168,138,0.03) 40%, transparent 65%)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-52 bg-gradient-to-t from-[#eee8dc] to-transparent" />

        <div className="relative z-10 mx-auto max-w-[1480px] px-4 sm:px-8">
          <div className="grid items-center gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:min-h-[72vh]">
            <div className="pt-6 lg:pt-0">
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease }}
                className="mb-7 font-body text-[11px] uppercase tracking-[0.28em] text-[#6e1f1b]/70"
              >
                Jpdz Labs
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.18, ease }}
                className="font-serif text-[clamp(3.2rem,7.5vw,8rem)] font-medium leading-[0.88] text-[#211d18]"
              >
                Frontier tools for
                <br className="hidden sm:block" />
                {' '}
                <span className="inline-block font-serif italic text-[#6e1f1b]" style={{ minWidth: '10ch' }}>
                  <Typewriter
                    text={["retrieval.", "memory.", "builders.", "research.", "shipping."]}
                    speed={70}
                    deleteSpeed={40}
                    waitTime={2200}
                    className="text-[#6e1f1b]"
                    cursorChar="_"
                    cursorClassName="ml-0.5 text-[#6e1f1b]/40"
                  />
                </span>
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease }}
                className="mt-10 max-w-xl border-t border-[#211d18]/10 pt-7"
              >
                <p className="max-w-lg font-body text-[17px] leading-[1.75] text-[#5f554b]">
                  TurboRAG, A/MCL, PKM, Jpdz, Skill Composer, and Alphagen — products that remove drag from the way developers retrieve, remember, reason, build, and understand markets.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <motion.a
                    href="#products"
                    whileHover={reduceMotion ? undefined : { y: -2 }}
                    whileTap={reduceMotion ? undefined : { scale: 0.98 }}
                    className="group inline-flex min-h-[3rem] items-center gap-3 bg-[#211d18] px-7 font-body text-[13px] font-semibold text-[#eee8dc] transition-all duration-300 hover:bg-[#6e1f1b] hover:shadow-[0_12px_40px_rgba(110,31,27,0.25)]"
                  >
                    Explore products
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1.5" />
                  </motion.a>
                  <motion.div whileHover={reduceMotion ? undefined : { y: -2 }} whileTap={reduceMotion ? undefined : { scale: 0.98 }}>
                    <Link
                      to="/developers"
                      className="inline-flex min-h-[3rem] items-center border border-[#211d18]/16 px-7 font-body text-[13px] font-semibold text-[#211d18] transition-all duration-300 hover:border-[#211d18]"
                    >
                      Developer notes
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, delay: 0.3, ease }}
              className="pointer-events-none relative hidden h-[600px] lg:block"
            >
              <HeroScene />
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* ── Marquee ── */}
      <Marquee />

      {/* ── Products ── */}
      <section id="products" className="relative border-b border-[#211d18]/8 bg-[#f7f1e7] py-24 sm:py-32">
        <div className="pointer-events-none absolute inset-0 opacity-30" style={{ backgroundImage: 'linear-gradient(90deg, rgba(33,29,24,0.03) 1px, transparent 1px), linear-gradient(rgba(33,29,24,0.02) 1px, transparent 1px)', backgroundSize: '64px 64px' }} />
        <div className="mx-auto max-w-[1480px] px-4 sm:px-8">
          <div className="mb-16 grid gap-8 lg:mb-20 lg:grid-cols-[0.72fr_1.28fr] lg:items-end">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.75, ease }}
            >
              <p className="mb-5 font-body text-[11px] uppercase tracking-[0.24em] text-[#6e1f1b]/60">
                Product portfolio
              </p>
              <h2 className="font-serif text-[clamp(2.8rem,5.5vw,6rem)] font-medium leading-[0.92] text-[#211d18]">
                Products and research, in one lab.
              </h2>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7, delay: 0.08, ease }}
              className="max-w-2xl font-body text-[17px] leading-[1.75] text-[#5f554b]"
            >
              TurboRAG, A/MCL, PKM, Jpdz, Skill Composer, and Alphagen share the same objective: remove drag from the way developers retrieve, remember, reason, build, and make sharper decisions.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: reduceMotion ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.85, ease }}
          >
            <ProductAtelier reduceMotion={Boolean(reduceMotion)} />
          </motion.div>
        </div>
      </section>

      {/* ── Thesis ── */}
      <section id="thesis" className="relative overflow-hidden bg-[#211d18] py-28 text-[#eee8dc] sm:py-36">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'linear-gradient(90deg, rgba(238,232,220,0.04) 1px, transparent 1px)',
            backgroundSize: '96px 96px'
          }}
        />
        <motion.div
          className="absolute left-[12%] top-[20%] hidden h-[28rem] w-[28rem] rounded-full lg:block"
          style={{ background: 'radial-gradient(circle, rgba(110,31,27,0.12) 0%, transparent 65%)' }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
        />

        <div className="relative mx-auto max-w-[1480px] px-4 sm:px-8">
          <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.75, ease }}
            >
              <p className="mb-5 font-body text-[11px] uppercase tracking-[0.24em] text-[#c8b89f]/55">
                Lab thesis
              </p>
              <h2 className="font-serif text-[clamp(2.8rem,5.5vw,5.5rem)] font-medium leading-[0.92] text-[#eee8dc]">
                Frontier work should land clean.
              </h2>
            </motion.div>

            <div className="grid gap-0">
              {thesis.map((item, index) => (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, delay: index * 0.08, ease }}
                  className="group border-t border-[#eee8dc]/8 py-8 transition-colors duration-500 first:border-t-0 hover:bg-[#eee8dc]/[0.03] lg:py-10"
                >
                  <div className="grid gap-3 lg:grid-cols-[4.5rem_1fr] lg:gap-5">
                    <span className="font-body text-[11px] uppercase tracking-[0.22em] text-[#6e1f1b]/70 lg:pt-2">{item.label}</span>
                    <div>
                      <h3 className="mb-3 font-serif text-[1.65rem] leading-snug text-[#eee8dc]">{item.title}</h3>
                      <p className="max-w-xl font-body text-[15px] leading-[1.8] text-[#d7cbb8]/60">{item.body}</p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Method ── */}
      <section className="relative bg-[#eee8dc] py-28 sm:py-36">
        <div className="pointer-events-none absolute left-[60%] top-[30%] hidden h-[400px] w-[400px] rounded-full lg:block" style={{ background: 'radial-gradient(circle, rgba(200,168,138,0.08) 0%, transparent 55%)' }} />
        <div className="mx-auto max-w-[1480px] px-4 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.75, ease }}
            className="mb-20 max-w-4xl"
          >
            <p className="mb-5 font-body text-[11px] uppercase tracking-[0.24em] text-[#6e1f1b]/60">
              Operating method
            </p>
            <h2 className="font-serif text-[clamp(2.8rem,5.5vw,6rem)] font-medium leading-[0.9] text-[#211d18]">
              Research hard. Build clean.
              <br className="hidden sm:block" /> Keep it usable.
            </h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-[3.8rem] top-4 hidden h-[calc(100%-2rem)] w-px bg-[#211d18]/12 lg:block" />

            {method.map(([verb, body], index) => (
              <motion.div
                key={verb}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: index * 0.08, ease }}
                className="group grid gap-4 border-b border-[#211d18]/8 py-10 last:border-b-0 lg:grid-cols-[7.5rem_1fr] lg:gap-6 lg:py-12"
              >
                <div className="relative">
                  <span className="font-serif text-[4.5rem] leading-none text-[#211d18]/[0.09] lg:text-[5rem]">0{index + 1}</span>
                  <div className="absolute left-[3.35rem] top-[1.1rem] hidden h-3 w-3 rounded-full border-2 border-[#211d18]/12 bg-[#eee8dc] transition-colors duration-500 group-hover:border-[#6e1f1b] group-hover:bg-[#6e1f1b]/10 lg:block" />
                </div>
                <div className="lg:pt-3">
                  <h3 className="mb-3 font-serif text-[2.2rem] leading-tight text-[#211d18] transition-colors duration-500 group-hover:text-[#6e1f1b]">{verb}</h3>
                  <p className="max-w-xl font-body text-[17px] leading-[1.75] text-[#5f554b]">{body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative overflow-hidden bg-[#6e1f1b] py-28 text-[#f7f1e7] sm:py-36">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'repeating-linear-gradient(55deg, transparent, transparent 40px, rgba(247,241,231,0.15) 40px, rgba(247,241,231,0.15) 41px)',
          }}
        />
        <div className="absolute left-0 right-0 top-0 h-24 bg-gradient-to-b from-[#211d18]/20 to-transparent" />
        <div className="absolute right-[-3%] top-1/2 hidden -translate-y-1/2 select-none font-serif text-[18vw] leading-none text-[#f7f1e7]/[0.06] lg:block">
          J
        </div>

        <div className="relative mx-auto max-w-[1480px] px-4 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end"
          >
            <h2 className="font-serif text-[clamp(3.2rem,7vw,7.5rem)] font-medium leading-[0.88]">
              Build closer to the frontier.
            </h2>
            <div className="max-w-xl">
              <p className="mb-10 font-body text-[17px] leading-[1.75] text-[#f7f1e7]/65">
                Start with Jpdz, explore the products, and use the lab as a practical edge for retrieval, memory, assistant workflows, and developer operations.
              </p>
              <Link
                to="/chat"
                className="group inline-flex min-h-14 items-center gap-4 bg-[#f7f1e7] px-8 font-body text-[13px] font-semibold text-[#211d18] transition-all duration-500 hover:bg-[#211d18] hover:text-[#f7f1e7] hover:shadow-[0_16px_50px_rgba(0,0,0,0.3)]"
              >
                Open Jpdz
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-2" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
